/**
 * AskBot Service
 *
 * Four-track pipeline, all routed by `processAskBotQuery`:
 *  DIRECTORY  → Backend API → banker contacts, channel partners, DSA partners,
 *               payout grids. Deterministically rendered server-side.
 *  PRODUCT    → Backend API → product_notes_system + the `products` catalog.
 *  ANALYTICAL → Backend API → DuckDB (LLM-generated SQL, access control
 *               injected server-side).
 *  CHAINED    → Same as ANALYTICAL; the JOIN returns all needed detail at once.
 *
 * Deterministic pre-routers run first (zero LLM cost); anything they don't
 * recognise goes to a Gemini intent classifier.
 *
 * NOTE: this service no longer touches Firestore. Contact lookups used to
 * generate a query plan with an LLM and execute it in the browser; that ran
 * per-front-end, duplicated the access-control rules client-side, and needed a
 * second LLM pass over the rows to write the answer. It now lives in
 * chatbot/core/directory.ts, shared with the WhatsApp bot.
 */

import { Capacitor, CapacitorHttp } from "@capacitor/core";

// ---------------------------------------------------------------------------
// Cross-origin POST helper — native CORS bypass
// ---------------------------------------------------------------------------
//
// CapacitorHttp is DISABLED globally (see capacitor.config.ts) so it doesn't
// patch fetch/XHR — that patch breaks Firestore's WebChannel transport. But the
// chatbot API lives on a different origin (dev/stag/prod.lendingschool.in), and
// the Android WebView origin (https://localhost) makes these calls cross-origin
// and therefore subject to CORS. Rather than depend on server-side CORS for the
// native app, route these specific POSTs through the CapacitorHttp plugin
// directly on native platforms (a native OkHttp request — no CORS). Calling the
// plugin API directly works even with the global patch disabled; the web build
// keeps using fetch. Both askbot endpoints are non-streaming, so buffering the
// native response is fine.

interface JsonPostResult {
  ok: boolean;
  status: number;
  json<T>(): Promise<T>;
}

async function postJson(url: string, body: unknown): Promise<JsonPostResult> {
  if (Capacitor.isNativePlatform()) {
    const res = await CapacitorHttp.post({
      url,
      headers: { "Content-Type": "application/json" },
      data: body,
    });
    return {
      ok: res.status >= 200 && res.status < 300,
      status: res.status,
      json: <T>() => Promise.resolve(res.data as T),
    };
  }

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return {
    ok: res.ok,
    status: res.status,
    json: <T>() => res.json() as Promise<T>,
  };
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface UserSecurityContext {
  userId: string;
  roles: string[];
  primaryRole: string;
  officeId?: string;
}

export interface ConversationTurn {
  role: "user" | "assistant";
  /** Plain text — markdown stripped before storing */
  content: string;
}

export type AskBotProgressStep =
  | "classifying"
  | "planning"
  | "fetching"
  | "responding";
export type AskBotProgressCallback = (
  step: AskBotProgressStep,
  data?: AskBotResult["data"],
) => void;
export type AskBotChunkCallback = (chunk: string) => void;

export type ResultType = "aggregate" | "ranked_list" | "list" | "team_report";

export interface ProductSource {
  fileName: string;
  url?: string;
  sourceType: string;
}

/**
 * Opaque slot-filling state carried between turns.
 *
 * The chat UI is stateless across renders, so when the bot asks "which city?"
 * the caller must hand this back on the next query. `token` is signed by the
 * server; `intent` is exposed so the client can pin routing without unpacking it.
 */
export interface AskBotClarification {
  token: string;
  intent: DirectoryIntent;
}

export interface AskBotResult {
  answer: string;
  recordCount: number;
  intent: string;
  followUpQuestions: string[];
  data?: {
    rows: Record<string, unknown>[];
    resultType: ResultType;
    entity?: string;
  };
  sources?: ProductSource[];
  /**
   * Bank + loan type the PRODUCT track resolved, present only when it resolved
   * both and actually found notes. Lets the UI offer a jump into the Banks &
   * Products page for the same record — the product track returns prose rather
   * than rows, so there is no record card to hang an Open button off.
   */
  productRef?: { bank: string; loanType: string };
  /**
   * Present when the bot asked a follow-up question. Pass it back into the
   * next `processAskBotQuery` call as `clarificationState`; drop it once the
   * user changes topic.
   */
  clarification?: AskBotClarification;
}

// Role helpers and ownership-field guards used to live here, for the
// browser-side Firestore LOOKUP track. That track now runs on the server —
// see chatbot/core/access.ts, which enforces the same firestore.rules matrix
// with the Admin SDK (which bypasses those rules and so must reproduce them).

// ---------------------------------------------------------------------------
// Language detection (English vs Hinglish / Roman Hindi)
// ---------------------------------------------------------------------------

export type InputLanguage = "english" | "hinglish";

const HINGLISH_MARKERS = new Set([
  "mera",
  "meri",
  "mere",
  "mujhe",
  "muje",
  "hamara",
  "hamari",
  "hamare",
  "aapka",
  "aapki",
  "aapke",
  "aap",
  "dikhao",
  "dikha",
  "batao",
  "bata",
  "dikhaiye",
  "bataye",
  "dedo",
  "karo",
  "karna",
  "dekho",
  "bhejo",
  "lao",
  "kya",
  "kaun",
  "kab",
  "kaise",
  "kahan",
  "kitne",
  "kitna",
  "kal",
  "aaj",
  "abhi",
  "agle",
  "pichle",
  "hafte",
  "mahine",
  "din",
  "hai",
  "hain",
  "tha",
  "thi",
  "nahi",
  "nahin",
  "mat",
  "aur",
  "ya",
  "lekin",
  "magar",
  "toh",
  "se",
  "ka",
  "ki",
  "ke",
  "jo",
  "jis",
  "jin",
  "wala",
  "wali",
  "wale",
  "sab",
  "sabhi",
  "kuch",
  "koi",
  "sare",
  "saare",
  "sabse",
  "zyada",
  "kam",
  "naya",
  "naye",
  "nayi",
  "purana",
  "purane",
  "un",
  "yeh",
  "woh",
]);

export function detectLanguage(text: string): InputLanguage {
  const words = text.toLowerCase().split(/[\s,!?.;:]+/);
  const hits = words.filter((w) => HINGLISH_MARKERS.has(w)).length;
  return hits >= 2 ? "hinglish" : "english";
}

// ---------------------------------------------------------------------------
// Product query pre-router — zero LLM cost deterministic check
// ---------------------------------------------------------------------------

const BANK_TOKENS = new Set([
  // Public sector
  "sbi",
  "state bank",
  "pnb",
  "punjab national",
  "bob",
  "bank of baroda",
  "boi",
  "bank of india",
  "canara",
  "union bank",
  "uco",
  "central bank",
  "indian bank",
  "idbi",
  "iob",
  "indian overseas",
  "psb",
  "punjab sind",
  "bom",
  "mahabank",
  "bank of maharashtra",
  "bank of maharastra",
  // Private
  "hdfc",
  "icici",
  "axis",
  "kotak",
  "kotak mahindra",
  "yes bank",
  "idfc",
  "rbl",
  "indusind",
  "federal",
  "federal bank",
  "sib",
  "south indian",
  "kvb",
  "karur vysya",
  "cub",
  "city union",
  "bandhan",
  "jkb",
  "jk bank",
  "dcb",
  "tmb",
  "tamilnad",
  // Foreign
  "citi",
  "citibank",
  "hsbc",
  "scb",
  "standard chartered",
  "deutsche",
  // NBFCs / HFCs
  "bajaj",
  "bajaj finance",
  "tata capital",
  "piramal",
  "aditya birla",
  "lic housing",
  "pnb housing",
  "mahindra finance",
  "l&t finance",
  "lt finance",
  "shriram",
  "iifl",
  "indiabulls",
  "reliance home",
  "hinduja",
]);

const LOAN_TYPE_TOKENS = new Set([
  "personal loan",
  "home loan",
  "lap",
  "loan against property",
  "business loan",
  "car loan",
  "auto loan",
  "vehicle loan",
  "gold loan",
  "education loan",
  "credit card",
  "overdraft",
  "working capital",
  "pl",
  "hl",
  "bl",
  "cl",
  "gl",
  "el",
]);

const ANALYTICS_SIGNAL_TOKENS = new Set([
  "how many",
  "total",
  "top",
  "most",
  "least",
  "count",
  "average",
  "best performing",
  "conversion",
  "kitne",
  "kitna",
  "sabse zyada",
  "trend",
  "monthly",
  "quarterly",
]);

// Words that signal CRM data intent — bare loan type without these → product track.
const CRM_ENTITY_TOKENS = new Set([
  "leads",
  "lead",
  "applications",
  "application",
  "cases",
  "case",
  "records",
  "customers",
  "customer",
  "banker",
  "bankers",
  "agent",
  "agents",
  "partner",
  "partners",
]);

const PRODUCT_SIGNAL_TOKENS = new Set([
  // Rate variants
  "interest rate",
  "rate of interest",
  "roi",
  "interest",
  "rate",
  "loan rate",
  "rate kya",
  "rate kya hai",
  "rate batao",
  // Eligibility
  "eligibility",
  "eligible",
  "eligibility criteria",
  // Documents
  "documents required",
  "documents needed",
  "document list",
  "documents",
  "docs",
  "kyc",
  "what documents",
  // Fees / charges
  "processing fee",
  "processing fees",
  "fees",
  "charges",
  "prepayment",
  "foreclosure",
  "penalty",
  // Tenure / LTV
  "tenure",
  "loan tenure",
  "repayment",
  "emi",
  "ltv",
  "loan to value",
  "maximum loan",
  "max loan",
  // Customer-facing
  "tell customer",
  "tell the customer",
  "explain to customer",
  "what to tell",
  "customer mein",
  "customer ko",
  // Product info
  "product details",
  "product info",
  "product notes",
  "scheme details",
  "scheme",
  "details",
  "info",
]);

/**
 * Matches `token` against `text` only on non-alphanumeric boundaries, so short
 * tokens never match inside ordinary words ("el" inside "ELigibility", "od"
 * inside "prODuct", "rate" inside "corpoRATE"). Without this, follow-ups get
 * mis-routed and product lookups resolve to the wrong loan type.
 * Mirrors `matchesAlias` in api/core/product.ts — keep them in sync.
 */
function matchesToken(text: string, token: string): boolean {
  const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(?<![a-z0-9])${escaped}(?![a-z0-9])`).test(text);
}

function isProductQuery(query: string): boolean {
  const lower = query.toLowerCase();
  const trimmedLower = lower.trim();

  // 1. "interest rate" / "rate of interest" alone is always a product question
  if (lower.includes("interest rate") || lower.includes("rate of interest"))
    return true;

  // 2. Bare bank name (exact match) → product (ask for loan type)
  const isBareBank = [...BANK_TOKENS].some((t) => trimmedLower === t);
  if (isBareBank) return true;

  // 3. Analytics signals → NOT product
  const hasAnalytics = [...ANALYTICS_SIGNAL_TOKENS].some((t) =>
    matchesToken(lower, t),
  );
  if (hasAnalytics) return false;

  // 4. CRM entity words → NOT product.
  //
  //    "customer" is the exception: beside a bank or loan type it reads as
  //    "what do I tell the customer about this product", not as a CRM lookup.
  //    Mirrors chatbot/core/product.ts — keep the two in step.
  const hasBank = [...BANK_TOKENS].some((t) => matchesToken(lower, t));
  const hasLoanType = [...LOAN_TYPE_TOKENS].some((t) => matchesToken(lower, t));
  const crmHits = [...CRM_ENTITY_TOKENS].filter((t) => matchesToken(lower, t));
  const onlyCustomer =
    crmHits.length > 0 &&
    crmHits.every((t) => t === "customer" || t === "customers");
  if (crmHits.length > 0 && !(onlyCustomer && (hasBank || hasLoanType)))
    return false;

  // 5. Bank name present → product (handles misspelled loan types; LLM classifier
  //    is the fallback on web, but aligning with WhatsApp logic here saves that call)
  if (hasBank) return true;

  // 6. Loan-type-based checks (no bank name)
  if (!hasLoanType) return false;
  if ([...PRODUCT_SIGNAL_TOKENS].some((t) => matchesToken(lower, t)))
    return true;
  // Bare loan type with no CRM entity → ask for bank
  return true;
}

/**
 * Attributes of a loan product a user asks about on their own once the product
 * is established. Mirrors PRODUCT_FOLLOWUP_WORDS in chatbot/core/product.ts.
 */
const PRODUCT_FOLLOWUP_TOKENS = [
  "roi", "rate", "rates", "interest", "interest rate", "tenure", "tenor",
  "eligibility", "eligible", "salary", "income", "cibil", "credit score",
  "bureau", "processing fee", "pf", "foir", "ltv", "margin", "documents",
  "documentation", "docs", "prepayment", "foreclosure", "part payment",
  "age limit", "max amount", "minimum salary", "scheme", "criteria",
];

/**
 * A bare attribute question that only makes sense against the product already
 * under discussion — "ROI?", "eligibility", "kitna salary chahiye".
 *
 * The product handler has always re-read the bank and loan type out of history,
 * but a query naming neither never reached it: `isProductQuery` returns false
 * and the message goes to the analytical track, which answers a product
 * question with "no matching records".
 *
 * Narrow on purpose — a short, essentially attribute-only query, and only once
 * the conversation has established a bank or loan type, so an opening "ROI?"
 * still asks which product. Mirrors `looksLikeProductFollowUp` on the server.
 */
function isProductFollowUp(query: string, history: ConversationTurn[]): boolean {
  const lower = query.toLowerCase();
  const words = lower.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0 || words.length > 6) return false;
  if (!PRODUCT_FOLLOWUP_TOKENS.some((t) => matchesToken(lower, t))) return false;

  return history.some((turn) => {
    if (turn.role !== "user") return false;
    const prior = turn.content.toLowerCase();
    return (
      [...BANK_TOKENS].some((t) => matchesToken(prior, t)) ||
      [...LOAN_TYPE_TOKENS].some((t) => matchesToken(prior, t))
    );
  });
}

// ---------------------------------------------------------------------------
// Directory pre-router
//
// MIRRORS `detectDirectoryIntent` in chatbot/core/directory.ts — keep the two
// in sync. The server is authoritative (it re-runs this and pins the intent);
// this copy exists purely so the client can skip the LLM classifier call for
// queries it can already recognise, exactly like `isProductQuery` above.
// ---------------------------------------------------------------------------

type DirectoryIntent =
  | "BANKER_DIRECTORY"
  | "PARTNER_DIRECTORY"
  | "DSA_DIRECTORY"
  | "PAYOUT_LOOKUP"
  | "PRODUCT_INFO";

const BANKER_ENTITY_TOKENS = new Set([
  "banker",
  "bankers",
  "bank contact",
  "bank contacts",
  "bank person",
  "bank people",
]);

const DESIGNATION_TOKENS = new Set([
  "relationship manager", "area sales manager", "regional sales manager",
  "cluster sales manager", "business development manager", "branch manager",
  "sales manager", "credit manager", "zonal manager", "operations manager",
  "branch head", "credit head", "vice president",
  "rm", "asm", "rsm", "csm", "bdm", "bm", "sm", "zm", "cm", "avp", "vp",
]);

const PAYOUT_TOKENS = new Set([
  "payout", "payouts", "commission", "brokerage", "payout rate", "payout rates",
  "payout structure", "payout grid", "slab", "slabs", "dsa code",
  "kitna payout", "payout kya", "payout kitna",
]);

const DSA_TOKENS = new Set(["dsa", "dsa partner", "dsa partners", "dsa contact", "dsa list"]);

const PARTNER_TOKENS = new Set([
  "channel partner", "channel partners", "cp", "partner", "partners",
  "connector", "connectors", "broker", "brokers", "builder", "builders",
  "vendor", "vendors",
]);

const LEAD_RECORD_TOKENS = new Set([
  "lead", "leads", "application", "applications", "case", "cases",
]);

/** Analytics vocabulary that applies to directory routing only. */
const DIRECTORY_ANALYTICS_TOKENS = new Set([
  "disbursed", "disbursal", "disbursement", "disbursements",
  "sourced", "sourcing", "performance", "target", "targets",
  "login", "logins", "leaderboard", "ranking", "rank",
  "breakdown", "compare", "comparison", "vs", "versus",
  "pipeline", "sabse kam", "highest", "lowest", "sum",
  "this month", "last month", "this week",
]);

const NOTE_TOKENS = new Set(["notes", "note"]);

const anyToken = (lower: string, tokens: Iterable<string>): boolean => {
  for (const t of tokens) if (matchesToken(lower, t)) return true;
  return false;
};

/**
 * Routes a query to a directory intent, or null to leave it on the existing
 * tracks. Order is load-bearing — see the server-side twin for the rationale
 * behind each step.
 */
function detectDirectoryIntentClient(query: string): DirectoryIntent | null {
  const lower = query.toLowerCase();

  if (anyToken(lower, ANALYTICS_SIGNAL_TOKENS)) return null;
  if (anyToken(lower, LEAD_RECORD_TOKENS)) return null;
  if (anyToken(lower, PAYOUT_TOKENS)) return "PAYOUT_LOOKUP";

  const analytical = anyToken(lower, DIRECTORY_ANALYTICS_TOKENS);

  if (!analytical) {
    if (anyToken(lower, BANKER_ENTITY_TOKENS)) return "BANKER_DIRECTORY";
    if (
      anyToken(lower, DESIGNATION_TOKENS) &&
      !anyToken(lower, PRODUCT_SIGNAL_TOKENS) &&
      !anyToken(lower, NOTE_TOKENS)
    ) {
      return "BANKER_DIRECTORY";
    }
  }

  if (isProductQuery(query)) return "PRODUCT_INFO";
  if (analytical) return null;

  if (anyToken(lower, DSA_TOKENS)) return "DSA_DIRECTORY";
  if (anyToken(lower, PARTNER_TOKENS)) return "PARTNER_DIRECTORY";

  return null;
}

// Pending-clarification detection and Firestore time placeholders lived here,
// for the browser-side LOOKUP track. Slot state is now structured and signed
// server-side — see chatbot/core/clarification.ts.

/**
 * Whether a message reads as the ANSWER to a question we just asked. Length is
 * the whole test: right after we ask something, a two-word reply is an answer,
 * and a full sentence is a new thought.
 */
function isSlotAnswerShaped(query: string): boolean {
  const words = query.trim().split(/\s+/).filter(Boolean);
  return words.length > 0 && words.length <= 3;
}

/**
 * Final intent for a turn, given what the pre-router detected and what we are
 * still waiting to hear.
 *
 * An explicit directory word in the reply always wins — "hdfc banker" after a
 * product clarification is a change of subject, not an answer. Everything else
 * short defers to the pending ask, because PRODUCT_INFO is what the router
 * reports for a bare bank name, which is exactly the shape of the answer to
 * "which bank or lender?" — and taking that at face value abandons the banker
 * question the user is in the middle of.
 *
 * Mirrors `resolvePendingIntent` in chatbot/core/clarification.ts. Keep the
 * two in step: if the client says DIRECTORY where the server says PRODUCT_INFO,
 * the directory route answers 422.
 */
function resolvePendingIntentClient(
  detected: DirectoryIntent | null,
  pendingIntent: DirectoryIntent | null,
  query: string,
): DirectoryIntent | null {
  if (!pendingIntent) return detected;
  if (detected && detected !== "PRODUCT_INFO") return detected;
  if (isSlotAnswerShaped(query)) return pendingIntent;
  return detected ?? pendingIntent;
}

// ---------------------------------------------------------------------------
// Gemini REST helper — multi-key / multi-model fallback
// ---------------------------------------------------------------------------

// Each model NAME is a SEPARATE free-tier daily quota bucket
// (20 req/day/model/project). Listing the "*-latest" aliases (which have their
// own buckets) multiplies daily capacity and provides quota-exhaustion fallback.
// Keep this list aligned with api/core/gemini.ts GEMINI_MODELS.
const GEMINI_MODELS = [
  "gemini-flash-lite-latest",
  "gemini-flash-latest",
  "gemini-2.5-flash-lite",
  "gemini-2.5-flash",
];

const GEMINI_BASE_URL =
  (import.meta.env.VITE_GEMINI_BASE_URL as string | undefined) ??
  "https://generativelanguage.googleapis.com/v1beta";

function getGeminiApiKeys(): string[] {
  const parseKeys = (raw: string | undefined): string[] =>
    raw
      ?.split(",")
      .map((k) => k.trim())
      .filter(Boolean) ?? [];

  const fromMulti = parseKeys(
    import.meta.env.VITE_GEMINI_API_KEYS as string | undefined,
  );
  const fromSingle = parseKeys(
    import.meta.env.VITE_GEMINI_API_KEY as string | undefined,
  );
  return Array.from(new Set([...fromMulti, ...fromSingle]));
}

async function callGemini(prompt: string): Promise<string> {
  const apiKeys = getGeminiApiKeys();
  if (apiKeys.length === 0) {
    throw new Error(
      "Gemini API key not configured. Add VITE_GEMINI_API_KEYS to your .env file.",
    );
  }

  let lastError = "";

  for (let ki = 0; ki < apiKeys.length; ki++) {
    const apiKey = apiKeys[ki];
    let keyExhausted = false;

    for (const model of GEMINI_MODELS) {
      const url = `${GEMINI_BASE_URL}/models/${model}:generateContent?key=${apiKey}`;

      let res: Response;
      try {
        res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contents: [{ parts: [{ text: prompt }] }],
            generationConfig: { temperature: 0.1, maxOutputTokens: 1200 },
          }),
        });
      } catch (err) {
        lastError = `Network error on key${ki + 1}/${model}: ${
          err instanceof Error ? err.message : String(err)
        }`;
        console.warn(`[AskBot] ${lastError}`);
        continue;
      }

      if (!res.ok) {
        const errBody = await res.text();
        console.warn(
          `[AskBot] key${ki + 1}/${model} → HTTP ${res.status}:`,
          errBody.slice(0, 200),
        );

        const isInvalidKey =
          res.status === 401 ||
          res.status === 403 ||
          (res.status === 400 && errBody.includes("API_KEY_INVALID"));

        if (isInvalidKey) {
          lastError = `key${ki + 1} invalid (${res.status})`;
          keyExhausted = true;
          break;
        }

        if (res.status === 429) {
          lastError = `key${ki + 1}/${model} rate-limited`;
          continue;
        }

        if (res.status === 404 || res.status === 400) {
          // Unknown/retired model — skip to the next one, don't abort.
          lastError = `key${ki + 1}/${model} unavailable (${res.status})`;
          continue;
        }

        throw new Error(
          `Gemini API error on ${model} (${res.status}): ${errBody}`,
        );
      }

      const data = (await res.json()) as {
        candidates?: { content?: { parts?: { text?: string }[] } }[];
      };
      return data.candidates?.[0]?.content?.parts?.[0]?.text ?? "";
    }

    if (keyExhausted) continue;
  }

  throw new Error(
    `Gemini API failed across all keys/models. Last error: ${lastError}`,
  );
}

// ---------------------------------------------------------------------------
// Gemini streaming REST helper
// ---------------------------------------------------------------------------

async function* callGeminiStream(
  prompt: string,
  temperature = 0.2,
): AsyncGenerator<string> {
  const apiKeys = getGeminiApiKeys();
  if (apiKeys.length === 0) throw new Error("Gemini API key not configured");

  let lastError = "";

  for (let ki = 0; ki < apiKeys.length; ki++) {
    const apiKey = apiKeys[ki];
    let keyExhausted = false;

    for (const model of GEMINI_MODELS) {
      const url = `${GEMINI_BASE_URL}/models/${model}:streamGenerateContent?key=${apiKey}&alt=sse`;

      let res: Response;
      try {
        res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contents: [{ parts: [{ text: prompt }] }],
            generationConfig: { temperature, maxOutputTokens: 1024 },
          }),
        });
      } catch (err) {
        lastError = `Network error key${ki + 1}/${model}: ${err instanceof Error ? err.message : String(err)}`;
        console.warn(`[AskBot] ${lastError}`);
        continue;
      }

      if (!res.ok) {
        const errBody = await res.text();
        const isInvalidKey =
          res.status === 401 ||
          res.status === 403 ||
          (res.status === 400 && errBody.includes("API_KEY_INVALID"));
        if (isInvalidKey) {
          lastError = `key${ki + 1} invalid (${res.status})`;
          keyExhausted = true;
          break;
        }
        if (res.status === 429) {
          lastError = `key${ki + 1}/${model} rate-limited`;
          continue;
        }
        if (res.status === 404 || res.status === 400) {
          lastError = `key${ki + 1}/${model} unavailable (${res.status})`;
          continue;
        }
        throw new Error(
          `Gemini streaming ${res.status}: ${errBody.slice(0, 200)}`,
        );
      }

      if (!res.body) {
        lastError = `No body from ${model}`;
        continue;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const jsonStr = line.slice(6).trim();
            if (!jsonStr || jsonStr === "[DONE]") continue;
            try {
              const data = JSON.parse(jsonStr) as {
                candidates?: { content?: { parts?: { text?: string }[] } }[];
              };
              const text = data.candidates?.[0]?.content?.parts?.[0]?.text;
              if (text) yield text;
            } catch {
              // skip malformed chunk
            }
          }
        }
        return; // stream finished successfully
      } catch (err) {
        lastError = `Read error ${model}: ${err instanceof Error ? err.message : String(err)}`;
        continue;
      }
    }

    if (keyExhausted) continue;
  }

  throw new Error(
    `Gemini streaming failed on all keys/models. Last: ${lastError}`,
  );
}

/** Robustly extracts a JSON object from raw LLM output (handles fences, surrounding text) */
function extractJSON(raw: string): string {
  const fenceMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (fenceMatch) return fenceMatch[1].trim();
  const jsonMatch = raw.match(/\{[\s\S]*\}/);
  if (jsonMatch) return jsonMatch[0];
  return raw.trim();
}

// ---------------------------------------------------------------------------
// Intent classification
// ---------------------------------------------------------------------------

type QueryIntent =
  | "DIRECTORY"
  | "LOOKUP"
  | "ANALYTICAL"
  | "CHAINED"
  | "PRODUCT"
  | "OUT_OF_SCOPE";

function formatHistoryForPrompt(
  history: ConversationTurn[],
  maxTurns = 6,
): string {
  if (history.length === 0) return "";
  const recent = history.slice(-maxTurns);
  const lines = recent.map(
    (t) =>
      `${t.role === "user" ? "User" : "Assistant"}: ${t.content.slice(0, 250)}`,
  );
  return `\nConversation history (use to resolve references like "their", "these", "the same ones"):\n${lines.join("\n")}\n`;
}

async function classifyIntent(
  userQuery: string,
  history: ConversationTurn[],
): Promise<QueryIntent> {
  const historyCtx = formatHistoryForPrompt(history.slice(-4), 4);

  const prompt = `You are a query classifier for a loan CRM. Classify the query as exactly one of: LOOKUP, ANALYTICAL, CHAINED, PRODUCT, or OUT_OF_SCOPE.

This CRM holds data about: leads (loan applicants), bankers (bank contacts), channel/DSA partners (referrers), and loan product notes (interest rates, eligibility, documents for specific bank + loan type combinations).

LOOKUP = fetch specific known records by name/ID, simple list of records, status of a specific lead
ANALYTICAL = aggregation, totals, counts, rankings, comparisons across records
  (keywords: "how many", "total", "most", "least", "top", "which", "compare", "average",
   "monthly", "quarterly", "kitne", "kitna", "sabse zyada", "sabse kam", "kul", "konsa",
   "kaunsa", "is mahine", "is quarter", "is saal", "pichle mahine", "zyada", "highest",
   "lowest", "ranking", "breakdown", "trend", "conversion")
CHAINED = needs analytical result first, then full contact/profile details of that top result
  (e.g. "contact of top DSA", "details of best performing banker")
PRODUCT = question about loan product details — interest rate, eligibility, documents, fees, tenure, LTV, scheme details for a specific loan type. A loan type is enough; bank name is optional (the system will ask if missing). "LAP" means Loan Against Property — a valid loan type.
  Examples: "HDFC home loan interest rate", "SBI personal loan eligibility", "ICICI LAP documents", "what is the LAP loan interest?", "home loan documents required", "What is the interest rate of LAP", "LAP interest rate", "interest rate for car loan", "what is interest rate"
  CRITICAL: any query containing "interest rate" or "rate of interest" is PRODUCT, never OUT_OF_SCOPE.
  NOT PRODUCT if it asks about counts/totals across products (that is ANALYTICAL).
OUT_OF_SCOPE = the query is NOT about this CRM's data. This includes:
  general knowledge, chit-chat, math unrelated to CRM data, coding help, weather, news,
  jokes, opinions, or requests to do/change things the CRM data cannot answer.
  Examples: "what's the weather", "write me a poem", "who is the prime minister",
  "tell me a joke", "how do I cook rice". Greetings like "hi"/"hello" are also OUT_OF_SCOPE.

CRITICAL IDENTIFIER RULE — overrides all other signals:
If the query provides a specific identifier to find ONE person or entity:
  - phone number / contact number (e.g. "9876553256", "contact: ...", "number: ...")
  - a person's name (e.g. "Rahul Sharma", "DSA Priya", "banker Amit")
  - a document/lead ID
  - an email address
→ ALWAYS classify as LOOKUP, regardless of words like "which", "what", "from", "where", "details", "bank"

CRITICAL PHRASING ROBUSTNESS: real users drop apostrophes, punctuation, and articles constantly
("Today leads" = "Today's leads", "Todays leads" = "Today's leads", "banker contact" = "banker's contact").
Judge intent by the entity + time/filter words present, never by exact grammar — a missing apostrophe or
possessive "s" must NEVER change the classification.

Examples:
  "Which bank is this banker from? contact: 9876553256" → LOOKUP (phone given)
  "What bank does Rahul Sharma work at?" → LOOKUP (name given)
  "Which bank has the most disbursements?" → ANALYTICAL (no specific person)
  "Show me details of DSA Rahul" → LOOKUP (name given)
  "Which DSA closed most leads this quarter?" → ANALYTICAL (no specific entity, aggregation)
  "Today leads" → LOOKUP (same as "Today's leads" — simple filtered list, no aggregation)
  "Todays leads" → LOOKUP (same as "Today's leads")
  "Show today leads" → LOOKUP
  "banker contact details" → LOOKUP (list/find banker records)
  "banker contact" → LOOKUP (same as "banker's contact")
  "HDFC banker contact number" → LOOKUP (bank + banker + contact identifier)
  "What is the total loan amount this month?" → ANALYTICAL (no specific entity)
  "Kitne leads is mahine disbursed hue?" → ANALYTICAL (count + date, Hinglish)
  "Sabse zyada disbursement kis banker ne kiya?" → ANALYTICAL (ranking, Hinglish)
  "Is quarter mein total kitna disburse hua?" → ANALYTICAL (aggregate + date, Hinglish)
  "DSA Priya ka number dikhao" → LOOKUP (specific person, Hinglish)
  "Sabse zyada leads wale DSA ke details dikhao" → CHAINED (rank then profile, Hinglish)
  "Conversion rate kya hai?" → ANALYTICAL (metric, Hinglish)

When ambiguous but plausibly about CRM data → ANALYTICAL
Only use OUT_OF_SCOPE when the query is clearly unrelated to leads/bankers/partners/product data.
${historyCtx}
Query: "${userQuery}"

Respond with ONLY one word: LOOKUP, ANALYTICAL, CHAINED, PRODUCT, or OUT_OF_SCOPE`;

  const raw = await callGemini(prompt);
  const result = raw.trim().toUpperCase().split(/\s/)[0];
  if (
    result === "LOOKUP" ||
    result === "ANALYTICAL" ||
    result === "CHAINED" ||
    result === "PRODUCT" ||
    result === "OUT_OF_SCOPE"
  ) {
    return result;
  }
  return "ANALYTICAL";
}

// ---------------------------------------------------------------------------
// Schema context for Firestore LOOKUP query plan generation
// ---------------------------------------------------------------------------

// The Firestore query-plan schema, plan generator and browser-side executor
// lived here. Directory lookups now run server-side against typed records —
// see chatbot/core/directory.ts and chatbot/core/directory-store.ts. That also
// removed the createdBy/sharedWith dual-query merge this file used to need,
// because Firestore cannot OR an == with an array-contains.

// ---------------------------------------------------------------------------
// ANALYTICAL track — call the backend API server
// ---------------------------------------------------------------------------

interface AnalyticalApiResponse {
  rows: Record<string, unknown>[];
  rowCount: number;
  sql: string;
  suspicious?: boolean;
}

async function callAnalyticalAPI(
  userQuery: string,
  ctx: UserSecurityContext,
  history: ConversationTurn[],
): Promise<AnalyticalApiResponse> {
  const res = await postJson(
    `${import.meta.env.VITE_CHATBOT_URL}/api/askbot/analytical`,
    {
      query: userQuery,
      history: history.slice(-6).map((t) => ({
        role: t.role,
        content: t.content.slice(0, 200),
      })),
      userId: ctx.userId,
      officeId: ctx.officeId,
      primaryRole: ctx.primaryRole,
    },
  );

  if (!res.ok) {
    const errBody = await res
      .json<{ error: string }>()
      .catch(() => ({ error: `HTTP ${res.status}` }));
    throw new Error(
      errBody.error ||
        `Analytical query failed (${res.status}). Is the API server running? Run: npm run start:api`,
    );
  }

  return res.json<AnalyticalApiResponse>();
}

// ---------------------------------------------------------------------------
// PRODUCT track — call the backend product API
// ---------------------------------------------------------------------------

interface ProductApiResponse {
  answer: string;
  sources: ProductSource[];
  notFound?: boolean;
  extractedBank?: string | null;
  extractedLoanType?: string | null;
  needsClarification?: {
    missing: string[];
    prompt: string;
  };
}

async function callProductAPI(
  userQuery: string,
  ctx: UserSecurityContext,
  history: ConversationTurn[],
  language: InputLanguage,
): Promise<ProductApiResponse> {
  const res = await postJson(
    `${import.meta.env.VITE_CHATBOT_URL}/api/askbot/product`,
    {
      query: userQuery,
      history: history.slice(-6).map((t) => ({
        role: t.role,
        content: t.content.slice(0, 200),
      })),
      userId: ctx.userId,
      officeId: ctx.officeId,
      primaryRole: ctx.primaryRole,
      language,
    },
  );

  if (!res.ok) {
    const errBody = await res
      .json<{ error: string }>()
      .catch(() => ({ error: `HTTP ${res.status}` }));
    throw new Error(
      errBody.error ||
        `Product query failed (${res.status}). Is the API server running? Run: npm run start:api`,
    );
  }

  return res.json<ProductApiResponse>();
}

// ---------------------------------------------------------------------------
// Directory track — banker contacts, channel partners, DSA partners, payouts
//
// This replaces the former client-side LOOKUP track, which generated a
// Firestore query plan with an LLM and executed it in the browser. Everything
// now runs server-side (chatbot/core/directory.ts) so both front-ends share
// one implementation, one access-control policy, and one set of reply formats.
// ---------------------------------------------------------------------------

interface DirectoryApiResponse {
  intent: DirectoryIntent;
  /** Ready-to-render Markdown, built deterministically on the server. */
  answer: string;
  rows: Record<string, unknown>[];
  recordCount: number;
  notFound: boolean;
  resolved: Record<string, string>;
  needsClarification?: {
    missing: string[];
    prompt: string;
    intent: DirectoryIntent;
    filled: Record<string, string>;
    options?: string[];
  };
  clarificationToken?: string;
}

async function callDirectoryAPI(
  userQuery: string,
  ctx: UserSecurityContext,
  language: InputLanguage,
  clarificationContext?: string,
): Promise<DirectoryApiResponse> {
  const res = await postJson(
    `${import.meta.env.VITE_CHATBOT_URL}/api/askbot/directory`,
    {
      query: userQuery,
      // Role and office are deliberately NOT sent: the server re-reads them
      // from approvedUsers, because these collections are ownership-scoped and
      // the server reads Firestore with the Admin SDK.
      userId: ctx.userId,
      language,
      clarificationContext,
    },
  );

  if (!res.ok) {
    const errBody = await res
      .json<{ error: string }>()
      .catch(() => ({ error: `HTTP ${res.status}` }));
    throw new Error(
      errBody.error ||
        `Directory query failed (${res.status}). Is the API server running? Run: npm run start:api`,
    );
  }

  return res.json<DirectoryApiResponse>();
}

/** Maps a directory intent onto the entity label used by the result cards. */
const DIRECTORY_ENTITY: Record<DirectoryIntent, string> = {
  BANKER_DIRECTORY: "bankers",
  PARTNER_DIRECTORY: "channelPartners",
  DSA_DIRECTORY: "dsaPartners",
  PAYOUT_LOOKUP: "payoutRates",
  PRODUCT_INFO: "products",
};

// Ordered by specificity — first matching aspect wins.
const PRODUCT_ASPECT_SIGNALS: Array<{
  signals: string[];
  followUps: [string, string];
}> = [
  {
    signals: [
      "interest rate",
      "rate of interest",
      "roi",
      "rate kya",
      "rate batao",
      "rate",
      "interest",
    ],
    followUps: [
      "What documents are required?",
      "What is the eligibility criteria?",
    ],
  },
  {
    signals: [
      "documents required",
      "documents needed",
      "document list",
      "documents",
      "docs",
      "kyc",
    ],
    followUps: [
      "What is the interest rate?",
      "What is the eligibility criteria?",
    ],
  },
  {
    signals: ["eligibility", "eligible"],
    followUps: ["What is the interest rate?", "What documents are required?"],
  },
  {
    signals: [
      "processing fee",
      "processing fees",
      "fees",
      "charges",
      "prepayment",
      "foreclosure",
      "penalty",
    ],
    followUps: ["What is the interest rate?", "What documents are required?"],
  },
  {
    signals: ["tenure", "emi", "repayment", "ltv", "maximum loan", "max loan"],
    followUps: ["What is the interest rate?", "What documents are required?"],
  },
];

const FOLLOW_UP_NC_KEYS: Record<string, string[]> = {
  "What is the interest rate?": ["interest"],
  "What documents are required?": ["document"],
  "What is the eligibility criteria?": ["eligibility", "eligible"],
  "What is the processing fee?": ["processing fee", "processing", "fee"],
  "What is the loan tenure?": ["tenure"],
  "What are the prepayment charges?": ["prepayment", "foreclosure"],
};

function filterNotCoveredFollowUps(
  followUps: readonly string[],
  notCoveredText: string,
): string[] {
  return followUps.filter((f) => {
    const keys = FOLLOW_UP_NC_KEYS[f] ?? [];
    return !keys.some((k) => notCoveredText.includes(k));
  });
}

function buildProductFollowUps(query: string, answerText?: string): string[] {
  // Capture the full sentence containing the "not available/covered" note — topics appear BEFORE the phrase
  const notCoveredText = answerText
    ? (answerText
        .toLowerCase()
        .match(/[^\n.]*not (?:available|covered)[^\n.]*/) ?? [""])[0]
    : "";

  const lower = query.toLowerCase();
  for (const { signals, followUps } of PRODUCT_ASPECT_SIGNALS) {
    if (signals.some((s) => lower.includes(s))) {
      const filtered = filterNotCoveredFollowUps(followUps, notCoveredText);
      return filtered.length > 0 ? filtered : [...followUps];
    }
  }
  const defaults = [
    "What documents are required?",
    "What is the eligibility criteria?",
  ];
  const filtered = filterNotCoveredFollowUps(defaults, notCoveredText);
  return filtered.length > 0 ? filtered : defaults;
}

const PUBLIC_BANK_NAMES = new Set([
  "State Bank of India",
  "Punjab National Bank",
  "Bank of Baroda",
  "Bank of India",
  "Canara Bank",
  "Union Bank of India",
  "UCO Bank",
  "Central Bank of India",
  "Indian Bank",
  "IDBI Bank",
  "Indian Overseas Bank",
  "Bank of Maharashtra",
  "Punjab & Sind Bank",
]);
const PRIVATE_BANK_NAMES = new Set([
  "HDFC Bank",
  "ICICI Bank",
  "Axis Bank",
  "Kotak Mahindra Bank",
  "Yes Bank",
  "IDFC First Bank",
  "RBL Bank",
  "IndusInd Bank",
  "Federal Bank",
  "South Indian Bank",
  "Bandhan Bank",
]);

function buildNotFoundProductFollowUps(
  bank: string | null,
  loanType: string | null,
): string[] {
  const lt = loanType ? loanType.replace(/_/g, " ") : null;
  const sfx = lt ? ` ${lt}` : " home loan";

  let alts: string[];
  if (bank && PUBLIC_BANK_NAMES.has(bank)) {
    const otherPublic = bank === "State Bank of India" ? "Canara Bank" : "SBI";
    alts = ["HDFC", "ICICI", otherPublic];
  } else if (bank && PRIVATE_BANK_NAMES.has(bank)) {
    const topPrivate = ["HDFC", "ICICI", "Axis Bank", "Kotak"];
    const others = topPrivate.filter(
      (n) => !bank.toLowerCase().startsWith(n.toLowerCase()),
    );
    alts = [...others.slice(0, 2), "SBI"];
  } else {
    alts = ["HDFC", "SBI", "Bajaj Finance"];
  }

  return alts.slice(0, 2).map((b) => `${b}${sfx}`);
}

/**
 * Follow-ups for a directory answer. These offer other QUESTIONS, never
 * alternative results — the caller suppresses them entirely on a not-found so
 * nothing is proposed in place of the record the user actually asked for.
 */
function buildDirectoryFollowUps(intent: DirectoryIntent): string[] {
  switch (intent) {
    case "BANKER_DIRECTORY":
      return [
        "Show payout rates for this bank",
        "Show product details for this bank",
      ];
    case "PARTNER_DIRECTORY":
      return ["Show my DSA partners", "Show banker contacts"];
    case "DSA_DIRECTORY":
      return ["Show my channel partners", "Show payout rates"];
    case "PAYOUT_LOOKUP":
      return [
        "Show banker contacts for this bank",
        "Show product details for this bank",
      ];
    default:
      return [];
  }
}

function buildAnalyticalFollowUps(query: string): string[] {
  const q = query.toLowerCase();

  if (
    q.includes("disburse") ||
    q.includes("disbursement") ||
    q.includes("amount")
  ) {
    return ["Which banker disbursed the most?", "Show my pending leads"];
  }
  if (q.includes("banker") || q.includes("bank")) {
    return [
      "What is the total disbursement this month?",
      "Show all leads for top bankers",
    ];
  }
  if (q.includes("channel partner") || q.includes("partner")) {
    return [
      "Show my top channel partners",
      "How many leads came from partners this month?",
    ];
  }
  if (q.includes("pending") || q.includes("new_lead") || q.includes("status")) {
    return ["How many leads disbursed this month?", "Who are the top bankers?"];
  }
  if (q.includes("lead") || q.includes("count") || q.includes("how many")) {
    return [
      "What is the total disbursement this month?",
      "Who are the top bankers?",
    ];
  }
  return ["Show my pending leads", "How much was disbursed this month?"];
}

function formatSourcesText(sources: ProductSource[]): string {
  if (!sources.length) return "";
  const lines = sources.map((s, i) => {
    if (s.url) {
      return `${i + 1}. [${s.fileName}](${s.url})`;
    }
    return `${i + 1}. ${s.fileName}`;
  });
  return `\n\n📎 **References**\n${lines.join("\n")}`;
}

// ---------------------------------------------------------------------------
// Result summarization — Firestore records
// ---------------------------------------------------------------------------

function formatAmount(n: unknown): string {
  if (typeof n !== "number") return "N/A";
  return `₹${n.toLocaleString("en-IN")}`;
}

// ---------------------------------------------------------------------------
// Item 9 — Structured analytical result handling
// ---------------------------------------------------------------------------

// ResultType is exported at the top of the file (near AskBotResult)

interface SummarisedResult {
  text: string;
  resultType: ResultType;
  displayedRows: number;
  totalRows: number;
}

function isPersonRecordRow(row: Record<string, unknown>): boolean {
  const cols = Object.keys(row).map((c) => c.toLowerCase());
  const RECORD_SIGNALS = [
    "phone",
    "mobile",
    "whatsapp",
    "name",
    "first_name",
    "last_name",
    "applicant_name",
    "status",
    "loan_type",
    "email",
    "customer_city",
  ];
  return RECORD_SIGNALS.some((sig) => cols.some((c) => c.includes(sig)));
}

function classifyResultType(
  rows: Record<string, unknown>[],
  query?: string,
): ResultType {
  if (rows.length === 0) return "aggregate";
  // A 1-row result that looks like a person/entity record → show as a card, not a metric
  if (rows.length === 1 && isPersonRecordRow(rows[0])) return "list";
  if (rows.length <= 1) return "aggregate";
  const cols = Object.keys(rows[0]).map((c) => c.toLowerCase());
  // Per-manager login+disbursement report
  // Primary: query-based signal (reliable regardless of Gemini's chosen column names)
  const isManagerQuery =
    !!query &&
    (/\b(each|every|per)\s+(sales[\s_]?)?manager\b/i.test(query) ||
      (/\blogin\b/i.test(query) &&
        /\bdisburs\b/i.test(query) &&
        /\bmanager\b/i.test(query)));
  // Secondary: column-based signal
  const hasManagerName = cols.some(
    (c) => c.includes("manager_name") || c.includes("manager"),
  );
  const hasLogins = cols.some((c) => /login/i.test(c) && !/target/i.test(c));
  const hasFiles = cols.some((c) => /total_files|total_leads/i.test(c));
  const hasDisbursed = cols.some(
    (c) => /disburs/i.test(c) && !/target/i.test(c),
  );
  if (
    (isManagerQuery || hasManagerName) &&
    (hasLogins || hasFiles) &&
    hasDisbursed
  )
    return "team_report";
  const hasAggregateCol = cols.some(
    (c) =>
      c.includes("total") ||
      c.includes("count") ||
      c.includes("sum") ||
      c.includes("avg") ||
      c.includes("rate") ||
      c.includes("pct") ||
      c.includes("percent") ||
      c.includes("conversion") ||
      c.includes("disburs"),
  );
  return hasAggregateCol ? "ranked_list" : "list";
}

function isMoneyCol(name: string): boolean {
  const n = name.toLowerCase();
  return (
    n.includes("amount") ||
    n.includes("payout") ||
    n.includes("total") ||
    n.includes("disburs") ||
    n.includes("sanction") ||
    n.includes("value")
  );
}

function isRateCol(name: string): boolean {
  const n = name.toLowerCase();
  return (
    n.includes("rate") ||
    n.includes("pct") ||
    n.includes("percent") ||
    n.includes("ratio")
  );
}

function fmtAnalyticalValue(colName: string, v: unknown): string {
  if (v === null || v === undefined) return "N/A";
  if (typeof v === "number") {
    if (isMoneyCol(colName)) return `₹${v.toLocaleString("en-IN")}`;
    if (isRateCol(colName)) return `${v}%`;
    return String(v);
  }
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v))
    return v.slice(0, 10);
  return String(v);
}

/** Format PostgreSQL rows for the response LLM. Returns structured metadata alongside text. */
function summariseAnalyticalRows(
  rows: Record<string, unknown>[],
  query?: string,
): SummarisedResult {
  if (rows.length === 0) {
    return {
      text: "(no records found)",
      resultType: "list",
      displayedRows: 0,
      totalRows: 0,
    };
  }

  const resultType = classifyResultType(rows, query);
  // Aggregates are typically 1 row; always show all. Lists cap at 20.
  const DISPLAY_CAP = resultType === "aggregate" ? rows.length : 20;
  const display = rows.slice(0, DISPLAY_CAP);

  let text: string;
  if (resultType === "aggregate" && display.length === 1) {
    // Single aggregate: flat key: value line (no row number prefix)
    text = Object.entries(display[0])
      .map(([k, v]) => `${k}: ${fmtAnalyticalValue(k, v)}`)
      .join(" | ");
  } else {
    text = display
      .map((row, i) => {
        const parts = Object.entries(row).map(
          ([k, v]) => `${k}: ${fmtAnalyticalValue(k, v)}`,
        );
        return `${i + 1}. ${parts.join(" | ")}`;
      })
      .join("\n");
  }

  return {
    text,
    resultType,
    displayedRows: display.length,
    totalRows: rows.length,
  };
}

// ---------------------------------------------------------------------------
// Response generation — Firestore results
// ---------------------------------------------------------------------------

// generateAnswer() streamed a second LLM pass over Firestore rows. Directory
// replies are now rendered deterministically on the server, so contact details
// and payout percentages are never paraphrased by a model.

// ---------------------------------------------------------------------------
// Response generation — PostgreSQL analytical results
// ---------------------------------------------------------------------------

async function generateAnalyticalAnswer(
  userQuery: string,
  rows: Record<string, unknown>[],
  language: InputLanguage,
  history: ConversationTurn[],
  suspicious: boolean,
  onChunk?: AskBotChunkCallback,
): Promise<{ answer: string; followUps: string[] }> {
  const {
    text: summary,
    resultType,
    displayedRows,
    totalRows,
  } = summariseAnalyticalRows(rows, userQuery);
  const count = rows.length;
  const truncationNote =
    totalRows > displayedRows
      ? `\nNote: ${totalRows} rows matched; only the first ${displayedRows} are shown. Mention that more exist and offer to narrow the query.`
      : "";
  const historyCtx = formatHistoryForPrompt(history.slice(-4), 4);

  const languageInstruction =
    language === "hinglish"
      ? `Respond in Hinglish (Roman Hindi characters, no Devanagari).
Example: "Is month mein 12 leads disburse hue hain, total ₹2,50,00,000 ka."`
      : `Respond in clear, professional English.`;

  // Item 9 — result-type-aware grounding instruction
  const resultTypeNote =
    resultType === "aggregate"
      ? `RESULT TYPE: Single computed aggregate — copy ALL figures VERBATIM. Do NOT re-derive, round, or convert to lakhs/crores unless the value itself is already in that form.`
      : resultType === "ranked_list"
        ? `RESULT TYPE: Ranked list — name the top performer(s) and state their exact metrics verbatim from the data. Do NOT invent additional context.`
        : `RESULT TYPE: Record list — present each record's key fields as listed.`;

  // Item 7 — self-verification note when all retries returned 0 rows on an aggregate query
  const suspiciousNote =
    count === 0 && suspicious
      ? `\n⚠️ SELF-VERIFICATION NOTE: The database returned 0 rows after two attempts. The most likely cause is an overly restrictive filter — wrong date range, status value mismatch, or a JSONB cast issue — NOT that the data genuinely doesn't exist. Acknowledge this honestly: say no matching data was found with the current filter, suggest trying a broader time range or different criteria, and do NOT report a figure of 0 as if it were the true answer.`
      : "";

  const answerPrompt = `You are AskBot, the assistant for DsaLens, a loan management platform.

${languageInstruction}
${historyCtx}
User asked: "${userQuery}"
${resultTypeNote}
The query returned ${count} row(s).${truncationNote}${suspiciousNote}

DATA (the ONLY source of truth — these are the actual query results):
${summary}

STRICT GROUNDING RULES — follow exactly, this is critical:
- Answer ONLY using the DATA above. Every number, name, and value in your reply must come directly from it. Copy values verbatim.
- For aggregate/total results: state the exact figure shown. NEVER re-compute, estimate, or convert to lakhs/crores — use the exact number as formatted in the DATA.
- NEVER invent, guess, estimate, or extrapolate any figure, name, bank, or detail not present in the DATA.
- If the DATA is empty (0 rows): clearly state that no matching data was found for this question/period/filter. Do not invent a "0" for a metric the query did not compute.
- If the DATA does not answer the question, say so honestly and suggest rephrasing. Do NOT fabricate.

PRESENTATION — produce clean Markdown (rendered in a styled chat UI):
- Start immediately with the key finding — NO greeting, NO "Got it", NO filler opener.
- For totals/metrics: one bold headline sentence with the exact figure (e.g. "**₹2.5 Cr** disbursed this month.").
- For rankings: name the top performer first in bold with their metric.
- Keep it to 2–3 sentences max. A rich card UI already shows full data — do NOT re-list every row.
- Format all amounts Indian-style: ₹1,00,000. Bold key values with **markdown**.
- Never mention SQL, databases, tables, or technical internals.
- Do NOT end with "Would you like me to…" — follow-up chips appear separately.

Respond ONLY with the natural conversational answer. No JSON. No preamble.`;

  // Stream the answer word-by-word (low temperature → factual, no embellishment)
  let fullAnswer = "";
  try {
    for await (const chunk of callGeminiStream(answerPrompt, 0.2)) {
      fullAnswer += chunk;
      onChunk?.(chunk);
    }
  } catch (err) {
    if (!fullAnswer) throw err;
    console.warn(
      "[AskBot] Analytical stream interrupted, using partial response",
    );
  }

  // Quick non-streaming call for follow-up suggestions
  const followUps = buildAnalyticalFollowUps(userQuery);

  return { answer: fullAnswer, followUps };
}

// ---------------------------------------------------------------------------
// Static (non-LLM) replies — for out-of-scope and graceful failures
// ---------------------------------------------------------------------------

/** Push a fixed message through the chunk callback so the UI renders it like a streamed answer. */
function emitStatic(message: string, onChunk?: AskBotChunkCallback): void {
  onChunk?.(message);
}

const OUT_OF_SCOPE_MESSAGE: Record<InputLanguage, string> = {
  english:
    "I'm AskBot — I can help with your DsaLens data: **leads**, **bankers**, **channel/DSA partners**, and **loan product notes** (interest rates, eligibility, documents for any bank + loan type). " +
    'I can\'t answer that one. Try: *"Show my pending leads"*, *"HDFC personal loan interest rate"*, or *"What documents for ICICI home loan?"*',
  hinglish:
    "Main AskBot hoon — main aapke DsaLens data mein madad kar sakta hoon: **leads**, **bankers**, **channel/DSA partners**, aur **loan product notes** (interest rates, eligibility, documents). " +
    'Yeh sawaal main answer nahi kar sakta. Aise puchhiye: *"Mere pending leads dikhao"* ya *"HDFC personal loan ka interest rate kya hai?"*',
};

/**
 * Maps a thrown error to an honest, user-facing message. We never show raw
 * SQL/DB/LLM errors, but we DO distinguish causes so the advice is accurate:
 * a rate limit is not fixed by rephrasing, and a server being down is not the
 * user's fault.
 */
function buildFailureMessage(err: unknown, language: InputLanguage): string {
  const raw = (err instanceof Error ? err.message : String(err)).toLowerCase();

  const isBusy =
    raw.includes("429") ||
    raw.includes("rate") ||
    raw.includes("quota") ||
    raw.includes("resource_exhausted") ||
    raw.includes("overload") ||
    raw.includes("503") ||
    raw.includes("failed on all keys") ||
    raw.includes("failed across all keys");

  const isConfig = raw.includes("not configured") || raw.includes("api key");

  const isUnreachable =
    raw.includes("failed to fetch") ||
    raw.includes("networkerror") ||
    raw.includes("api server") ||
    raw.includes("is the api server running");

  if (isBusy) {
    return language === "hinglish"
      ? "Abhi AI service kaafi busy hai (rate limit). Thodi der baad dobara try kijiye — aapka sawaal theek tha."
      : "The AI service is busy right now (rate limit reached). Please wait a moment and try again — your question was fine.";
  }
  if (isConfig) {
    return language === "hinglish"
      ? "AI service abhi set up nahi hai (API key missing). Kripya administrator se contact kijiye."
      : "The AI service isn't configured yet (missing API key). Please contact your administrator.";
  }
  if (isUnreachable) {
    return language === "hinglish"
      ? "Abhi analytics service se connect nahi ho pa raha. Thodi der baad dobara try kijiye."
      : "I can't reach the analytics service right now. Please try again in a moment.";
  }
  return language === "hinglish"
    ? "Maaf kijiye, abhi main is sawaal ka sahi jawaab nahi de paaya. Kripya thoda alag tarah se puchhiye, ya leads / bankers / channel partners ke baare mein zyada specific puchhiye."
    : "Sorry, I couldn't answer that accurately right now. Please try rephrasing it, or ask something more specific about your leads, bankers, or channel partners.";
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function processAskBotQuery(
  userQuery: string,
  ctx: UserSecurityContext,
  history: ConversationTurn[] = [],
  onProgress?: AskBotProgressCallback,
  onChunk?: AskBotChunkCallback,
  clarificationState?: AskBotClarification,
): Promise<AskBotResult> {
  const language = detectLanguage(userQuery);

  // Stage 0: deterministic pre-routers (zero LLM cost).
  //
  // A live clarification pins the intent — when the bot asked "which city?",
  // the reply is "Mumbai", which carries no routable signal of its own. This
  // replaces the old approach of substring-matching the previous assistant
  // message, which coupled routing to reply wording.
  //
  // A short reply outranks the pre-router, rather than only filling in for it:
  // "HDFC" answering "which bank or lender?" reads as a bare-bank PRODUCT
  // query, so letting detection win would hand back product notes and abandon
  // the banker question. Mirrors the server (core/clarification.ts).
  const pendingIntent = clarificationState?.intent ?? null;
  const directoryIntent = resolvePendingIntentClient(
    detectDirectoryIntentClient(userQuery),
    pendingIntent,
    userQuery,
  );

  let intent: QueryIntent =
    directoryIntent === "PRODUCT_INFO"
      ? "PRODUCT"
      : directoryIntent
        ? "DIRECTORY"
        : isProductFollowUp(userQuery, history)
          ? "PRODUCT"
          : "ANALYTICAL";

  // Step 1: Classify intent via LLM (skip if a deterministic pre-router already fired)
  if (intent !== "PRODUCT" && intent !== "DIRECTORY") {
    onProgress?.("classifying");
    try {
      intent = await classifyIntent(userQuery, history);
    } catch (err) {
      console.error("[AskBot] Intent classification failed:", err);
      onProgress?.("responding");
      const msg = buildFailureMessage(err, language);
      emitStatic(msg, onChunk);
      return {
        answer: msg,
        recordCount: 0,
        intent: "OUT_OF_SCOPE",
        followUpQuestions: [],
      };
    }
  }
  // The classifier still knows the legacy LOOKUP label. Contact-style lookups
  // now belong to the directory; anything else it means by "lookup" (a single
  // lead, a phone number on an application) is served well by the SQL track.
  if (intent === "LOOKUP") {
    intent = detectDirectoryIntentClient(userQuery) ? "DIRECTORY" : "ANALYTICAL";
  }
  console.log(`[AskBot] Intent: ${intent}`);

  // ── Out-of-scope: decline honestly, no data fetch, no hallucination ──────
  if (intent === "OUT_OF_SCOPE") {
    onProgress?.("responding");
    const msg = OUT_OF_SCOPE_MESSAGE[language];
    emitStatic(msg, onChunk);
    return {
      answer: msg,
      recordCount: 0,
      intent,
      followUpQuestions: [],
    };
  }

  let answer: string;
  let followUps: string[] = [];
  let recordCount: number;
  let data: AskBotResult["data"];

  try {
    if (intent === "PRODUCT") {
      // ── Product track → backend product API → Firestore product_notes_system ──
      onProgress?.("fetching");
      const productResult = await callProductAPI(
        userQuery,
        ctx,
        history,
        language,
      );

      if (productResult.needsClarification) {
        onProgress?.("responding");
        emitStatic(productResult.answer, onChunk);
        return {
          answer: productResult.answer,
          recordCount: 0,
          intent,
          followUpQuestions: [],
          sources: [],
          // Pin the track for the reply. A slot answer ("Bengaluru", "HDFC")
          // carries no product signal of its own, so without this the next
          // turn is re-classified from scratch and can land on the SQL track.
          // No token: the product handler refills slots from `history`, so
          // there is no server-side state to carry.
          clarification: { token: "", intent: "PRODUCT_INFO" },
        };
      }

      onProgress?.("responding");
      emitStatic(productResult.answer, onChunk);

      if (productResult.sources?.length) {
        const sourcesText = formatSourcesText(productResult.sources);
        onChunk?.(sourcesText);
      }

      const productFollowUps = productResult.notFound
        ? buildNotFoundProductFollowUps(
            productResult.extractedBank ?? null,
            productResult.extractedLoanType ?? null,
          )
        : buildProductFollowUps(userQuery, productResult.answer);
      // Only offer the deep link when there is something to open: both slots
      // resolved, and the lookup actually landed on notes.
      const productRef =
        !productResult.notFound &&
        productResult.extractedBank &&
        productResult.extractedLoanType
          ? {
              bank: productResult.extractedBank,
              loanType: productResult.extractedLoanType,
            }
          : undefined;

      return {
        answer: productResult.answer,
        recordCount: productResult.sources?.length ?? 0,
        intent,
        followUpQuestions: productFollowUps,
        sources: productResult.sources,
        productRef,
      };
    } else if (intent === "DIRECTORY") {
      // ── Directory track → backend directory API ─────────────────────────
      //
      // The reply is rendered from the server's deterministic Markdown, not
      // streamed from a second LLM pass over the rows as the old LOOKUP track
      // did. Contact details and payout percentages are exactly the data a
      // model must not be free to paraphrase — the previous implementation
      // needed an explicit "NEVER invent a phone number" prompt rule precisely
      // because that path could.
      onProgress?.("fetching");
      const directoryResult = await callDirectoryAPI(
        userQuery,
        ctx,
        language,
        clarificationState?.token,
      );

      onProgress?.("responding");
      emitStatic(directoryResult.answer, onChunk);

      const clarification = directoryResult.clarificationToken
        ? { token: directoryResult.clarificationToken, intent: directoryResult.intent }
        : undefined;

      // No follow-up suggestions on a not-found or a clarification: the first
      // must not offer substitutes for the record the user asked for, and the
      // second needs a typed answer.
      const directoryFollowUps =
        directoryResult.notFound || directoryResult.needsClarification
          ? []
          : buildDirectoryFollowUps(directoryResult.intent);

      return {
        answer: directoryResult.answer,
        recordCount: directoryResult.recordCount,
        intent,
        followUpQuestions: directoryFollowUps,
        data: directoryResult.rows.length
          ? {
              rows: directoryResult.rows,
              resultType: "list",
              entity: DIRECTORY_ENTITY[directoryResult.intent],
            }
          : undefined,
        clarification,
      };
    } else {
      // ── Analytical / Chained track → PostgreSQL via backend API ─────────
      // CHAINED queries naturally produce JOIN SQL that returns all needed
      // detail fields (name, contact, etc.) alongside aggregate metrics.
      onProgress?.("planning");

      onProgress?.("fetching");
      const {
        rows,
        rowCount,
        suspicious = false,
      } = await callAnalyticalAPI(userQuery, ctx, history);
      recordCount = rowCount;
      data = { rows, resultType: classifyResultType(rows, userQuery) };

      onProgress?.("responding", data);
      ({ answer, followUps } = await generateAnalyticalAnswer(
        userQuery,
        rows,
        language,
        history,
        suspicious,
        onChunk,
      ));
    }
  } catch (err) {
    // The user must never see a raw SQL/DB/LLM error or a fabricated answer.
    // Log the real cause; show an honest, friendly "couldn't answer" message.
    console.error(`[AskBot] ${intent} track failed:`, err);
    onProgress?.("responding");
    const msg = buildFailureMessage(err, language);
    emitStatic(msg, onChunk);
    return { answer: msg, recordCount: 0, intent, followUpQuestions: [] };
  }

  return {
    answer,
    recordCount,
    intent,
    followUpQuestions: followUps,
    data,
  };
}
